import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import { POST, PUT, DELETE, GET, UPLOAD } from '../../../../configs/api';
import { api } from '../../../../utils/constants';
import { addJobTechnician, updateJobTechnician, deleteJobTechnician } from '../../../../store/slices/jobsSlice';
import { fetchAccountingTabCounts } from '../../../../store/slices/accountingCountsSlice';
import AddTechnicianDialog from '../AddTechnicianDialog';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
  isAccounting,
  canAccessAccounting,
  canLockJobTechnician,
  canUnlockJobTechnician,
  canEditLockedJobTechnician,
  canDeleteLockedJobTechnician,
  canUploadJobTechnicianAttachment,
  canDeleteJobTechnicianAttachment,
} from '../../../../utils/permissions';
import { isTechnicianAssignable } from '../../../../utils/technicianStatus';
import {
  getJobTechnicianStatusLabel,
  needsFreeOfChargeConfirm,
  FREE_OF_CHARGE_STATUS,
} from '../../../../utils/jobTechnicianRequests';
import { isInvoicedStatus } from '../../../../utils/jobStatuses';

const TECHNICIAN_STATUS_OPTIONS = [
  { value: 1, label: 'Dispatched' },
  { value: 2, label: 'On route' },
  { value: 3, label: 'Start' },
  { value: 4, label: 'Paid' },
  { value: 5, label: 'Request payment' },
  { value: 6, label: 'No show' },
  { value: 7, label: 'Reschedule' },
  { value: 8, label: 'Need part' },
  { value: FREE_OF_CHARGE_STATUS, label: 'Free of charge' },
];

// Helper function to get display name for technician/vendor
const getTechnicianDisplayName = (tech) => {
  if (!tech) return '';
  if (tech.type === 'vendor') {
    return tech.company_name || '';
  }
  return `${tech.first_name || ''} ${tech.last_name || ''}`.trim();
};

// Check if technician needs attention (no scheduled date or special status)
const technicianNeedsAttention = (tech) => {
  const scheduledDate = tech.scheduled_date;
  const hasNoScheduledDate = !scheduledDate || scheduledDate === '' || scheduledDate === null;
  
  // Check for specific statuses: Request payment (5), No show (6), Reschedule (7), Need part (8)
  const status = tech.status;
  const hasSpecialStatus = status === 5 || status === 6 || status === 7 || status === 8;
  
  return hasNoScheduledDate || hasSpecialStatus;
};

const formatAssignmentDateSummary = (iso) => {
  if (!iso) return '';
  const d = dayjs(iso);
  return d.isValid() ? d.format('MMM D, YYYY h:mm A') : '';
};

const formatTechCostSummary = (value) => {
  if (value === null || value === undefined) return '';
  const s = String(value).trim();
  if (s === '') return '';
  const n = Number(s);
  if (Number.isFinite(n)) {
    return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  }
  return `$${s}`;
};

// Get attention reasons for a technician
const getAttentionReasons = (tech) => {
  const scheduledDate = tech.scheduled_date;
  const hasNoScheduledDate = !scheduledDate || scheduledDate === '' || scheduledDate === null;
  const status = tech.status;
  const attentionReasons = [];
  
  if (hasNoScheduledDate) attentionReasons.push('No visit date');
  if (status === 5) attentionReasons.push('Request payment');
  if (status === 6) attentionReasons.push('No show');
  if (status === 7) attentionReasons.push('Reschedule');
  if (status === 8) attentionReasons.push('Need part');
  
  return attentionReasons;
};

// Check if user can lock (accounting / admin; not dispatcher)
const canLock = (user) => {
  return canLockJobTechnician(user);
};

// Check if user can unlock (owner only)
const canUnlock = (user) => {
  return canUnlockJobTechnician(user);
};

// Check if user can edit locked assignment (owner / GM / accountant)
const canEditLocked = (user) => {
  return canEditLockedJobTechnician(user);
};

const canDeleteLocked = (user) => {
  return canDeleteLockedJobTechnician(user);
};

// Check if user can upload attachments (owner/accountant only)
const canUploadAttachment = (user) => {
  return canUploadJobTechnicianAttachment(user);
};

// Check if user can delete attachments (owner only)
const canDeleteAttachment = (user) => {
  return canDeleteJobTechnicianAttachment(user);
};

// Check if lock prerequisites are met
const canBeLocked = (assignment) => {
  return assignment.paid_date && 
    assignment.tech_cost && 
    parseFloat(assignment.tech_cost) > 0;
};

const TechniciansTab = ({
  jobDetails,
  jobId,
  loadingAdditionalData,
  technicians, // users.technicians
  onRefreshJob,
  user,
  isJobLocked = false,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const dispatch = useDispatch();
  const isTabLocked = !!isJobLocked;
  
  // Local state for autocomplete input
  const [technicianInputValue, setTechnicianInputValue] = useState('');
  
  // Local state for assignments and operations
  const [assignments, setAssignments] = useState([]);
  const [savingStates, setSavingStates] = useState({});
  const [dateErrors, setDateErrors] = useState({});
  const [error, setError] = useState('');
  
  // Track original server values for comparison on blur
  const [serverValues, setServerValues] = useState({});
  
  // Attachments state
  const [attachments, setAttachments] = useState({});
  const [loadingAttachments, setLoadingAttachments] = useState({});
  const [uploadingAttachment, setUploadingAttachment] = useState({});
  const fileInputRefs = useRef({});
  
  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [previewAssignmentId, setPreviewAssignmentId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Add technician dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogValue, setDialogValue] = useState({ name: '' });

  const [freeOfChargeConfirm, setFreeOfChargeConfirm] = useState(null);
  const freeOfChargeConfirmingRef = useRef(false);
  const [pendingInvoicedTechEdit, setPendingInvoicedTechEdit] = useState(null);
  const [confirmingInvoicedTechEdit, setConfirmingInvoicedTechEdit] = useState(false);
  const invoicedTechEditConfirmingRef = useRef(false);

  // Ref for focusing add-technician input when card is clicked
  const autocompleteInputRef = useRef(null);

  // Initialize assignments from jobDetails
  useEffect(() => {
    if (jobDetails?.technicians) {
      setAssignments(jobDetails.technicians);
      // Store server values for comparison
      const values = {};
      jobDetails.technicians.forEach(tech => {
        values[tech.id] = {
          tech_cost: tech.tech_cost,
          notes: tech.notes
        };
      });
      setServerValues(values);
      
      // Initialize attachments from jobDetails if available
      if (jobDetails.technicianAttachments) {
        const attachmentsByTechId = {};
        jobDetails.technicianAttachments.forEach(attachment => {
          if (!attachmentsByTechId[attachment.entity_id]) {
            attachmentsByTechId[attachment.entity_id] = [];
          }
          attachmentsByTechId[attachment.entity_id].push(attachment);
        });
        setAttachments(attachmentsByTechId);
      }
    } else {
      setAssignments([]);
      setServerValues({});
    }
  }, [jobDetails]);

  // Fetch attachments for an assignment
  const fetchAttachments = async (assignmentId) => {
    if (!jobId) return;
    
    setLoadingAttachments(prev => ({ ...prev, [assignmentId]: true }));
    try {
      const { data, status } = await GET(
        `${api.getJobTechnicianAttachments}/${jobId}/technicians/${assignmentId}/attachments`
      );
      if (status === 200) {
        setAttachments(prev => ({ ...prev, [assignmentId]: data }));
      }
    } catch (err) {
      console.error('Error fetching attachments:', err);
    } finally {
      setLoadingAttachments(prev => ({ ...prev, [assignmentId]: false }));
    }
  };

  // Add technician assignment
  const handleAddTechnician = async (technician) => {
    if (!jobId) return;
    if (isTabLocked) return;
    
    setTechnicianInputValue('');
    setSavingStates(prev => ({ ...prev, adding: true }));
    setError('');

    try {
      const { data, status, message } = await POST(
        `${api.createJobTechnician}/${jobId}/technicians`,
        {},
        {
          technician_id: technician.id,
          tech_cost: 0,
          paid_date: null,
          scheduled_date: null,
          status: 1,
          notes: ''
        }
      );

      if (status === 201) {
        // Update Redux store with the new technician
        dispatch(addJobTechnician({ jobId, technician: data }));
        // Update local state
        setAssignments(prev => [data, ...prev]);
        // Store server values for the new technician
        setServerValues(prev => ({
          ...prev,
          [data.id]: {
            tech_cost: data.tech_cost,
            notes: data.notes
          }
        }));
      } else {
        setError(message || 'Failed to add technician');
        // Refresh job details on error to sync with server
        if (onRefreshJob) onRefreshJob();
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to add technician');
      console.error('Error adding technician:', err);
      // Refresh job details on error to sync with server
      if (onRefreshJob) onRefreshJob();
    } finally {
      setSavingStates(prev => ({ ...prev, adding: false }));
    }
  };

  // Dialog handlers for adding new technician
  const handleDialogClose = () => {
    setDialogValue({ name: '' });
    setDialogOpen(false);
    setTechnicianInputValue('');
  };

  const handleTechnicianAdded = (technician) => {
    // Add the newly created technician as an assignment
    handleAddTechnician(technician);
    handleDialogClose();
  };

  // Remove technician assignment
  const handleRemoveTechnician = async (assignmentId) => {
    if (!jobId) return;
    if (isTabLocked) return;

    const assignment = assignments.find(a => a.id === assignmentId);
    
    // Check if locked and user doesn't have permission
    if (assignment?.is_locked && !canDeleteLocked(user)) {
      setError('This technician assignment is locked. Only an owner can delete it.');
      return;
    }

    setSavingStates(prev => ({ ...prev, [assignmentId]: 'deleting' }));
    setError('');

    // Optimistic update
    const previousAssignments = [...assignments];
    setAssignments(prev => prev.filter(a => a.id !== assignmentId));

    try {
      const { status, message } = await DELETE(
        `${api.deleteJobTechnician}/${jobId}/technicians/${assignmentId}`
      );

      if (status === 200) {
        // Update Redux store
        dispatch(deleteJobTechnician({ jobId, technicianId: assignmentId }));
        // Remove from server values tracking
        setServerValues(prev => {
          const newValues = { ...prev };
          delete newValues[assignmentId];
          return newValues;
        });
        // Local state already updated optimistically
      } else {
        // Rollback on error
        setAssignments(previousAssignments);
        setError(message || 'Failed to delete technician assignment');
        // Refresh job details on error to sync with server
        if (onRefreshJob) onRefreshJob();
      }
    } catch (err) {
      // Rollback on error
      setAssignments(previousAssignments);
      setError('Failed to delete technician assignment');
      console.error('Error deleting technician:', err);
      // Refresh job details on error to sync with server
      if (onRefreshJob) onRefreshJob();
    } finally {
      setSavingStates(prev => {
        const newState = { ...prev };
        delete newState[assignmentId];
        return newState;
      });
    }
  };

  const invoicedTechEditNeedsWarn = () =>
    isTabLocked &&
    isInvoicedStatus(jobDetails?.status) &&
    !!(jobDetails?.quickbooks_invoiced || jobDetails?.sent_to_client);

  const getInvoicedTechEditWarningText = () => {
    const qb = !!jobDetails?.quickbooks_invoiced;
    const sent = !!jobDetails?.sent_to_client;
    const flags = [
      qb ? 'marked as QuickBooks invoiced' : null,
      sent ? 'marked as sent to the client' : null,
    ].filter(Boolean).join(' and ');
    return `This job is already ${flags}. Saving changes will update technician cost and Invoiced / Invoiced/Loss status in ServiceGeek. It will not update QuickBooks or any invoice already sent.`;
  };

  // Update technician assignment
  const handleUpdateTechnician = async (assignmentId, field, value, { skipInvoicedWarn = false } = {}) => {
    if (!jobId) return;
    if (isTabLocked && !isAccounting(user)) return;

    const assignment = assignments.find(a => a.id === assignmentId);
    
    // Check if locked and user doesn't have permission
    if (assignment?.is_locked && !canEditLocked(user)) {
      setError('This technician assignment is locked. Owner, general manager, or accountant can edit it.');
      return;
    }

    const isFreeOfChargeUpdate = field === 'status' && Number(value) === FREE_OF_CHARGE_STATUS;
    const costAffecting = field === 'tech_cost' || isFreeOfChargeUpdate;
    if (!skipInvoicedWarn && costAffecting && invoicedTechEditNeedsWarn()) {
      setPendingInvoicedTechEdit({ assignmentId, field, value });
      return;
    }

    // Optimistic update
    const previousAssignments = [...assignments];
    const isPaidStatusUpdate = field === 'status' && Number(value) === 4;
    const paidNow = new Date().toISOString();
    setAssignments(prev => prev.map(a => {
      if (a.id !== assignmentId) return a;
      if (isFreeOfChargeUpdate) {
        return {
          ...a,
          status: FREE_OF_CHARGE_STATUS,
          tech_cost: 0,
          paid_date: a.paid_date || paidNow,
          scheduled_date: a.scheduled_date || paidNow,
          is_locked: true,
          requested_unlock: false,
        };
      }
      if (isPaidStatusUpdate) {
        return {
          ...a,
          status: 4,
          paid_date: paidNow,
          scheduled_date: paidNow,
        };
      }
      return { ...a, [field]: value };
    }));

    setSavingStates(prev => ({ ...prev, [assignmentId]: 'saving' }));
    setError('');

    try {
      const updateData = isPaidStatusUpdate
        ? { status: 4, paid_date: paidNow, scheduled_date: paidNow }
        : { [field]: value };
      
      const { data, status, message } = await PUT(
        `${api.updateJobTechnician}/${jobId}/technicians/${assignmentId}`,
        {},
        updateData
      );

      if (status === 200) {
        // Update Redux store with the returned data
        dispatch(updateJobTechnician({ 
          jobId, 
          technicianId: assignmentId, 
          updatedData: data 
        }));
        // Update local state with server response to stay in sync
        setAssignments(prev => prev.map(a => 
          a.id === assignmentId ? data : a
        ));
        // Update server values tracking
        if (field === 'tech_cost' || field === 'notes' || isFreeOfChargeUpdate) {
          setServerValues(prev => ({
            ...prev,
            [assignmentId]: {
              ...prev[assignmentId],
              ...(isFreeOfChargeUpdate ? { tech_cost: 0 } : { [field]: value }),
            }
          }));
        }
        if (costAffecting) {
          if (onRefreshJob) onRefreshJob();
          if (canAccessAccounting(user)) {
            dispatch(fetchAccountingTabCounts());
          }
        }
      } else {
        // Rollback on error
        setAssignments(previousAssignments);
        setError(
          field === 'paid_date'
            ? 'Failed, please try again later'
            : (message || 'Failed to update technician assignment')
        );
        // Refresh job details on error to sync with server
        if (onRefreshJob) onRefreshJob();
      }
    } catch (err) {
      // Rollback on error
      setAssignments(previousAssignments);
      setError(
        field === 'paid_date'
          ? 'Failed, please try again later'
          : 'Failed to update technician assignment'
      );
      console.error('Error updating technician:', err);
      // Refresh job details on error to sync with server
      if (onRefreshJob) onRefreshJob();
    } finally {
      setSavingStates(prev => {
        const newState = { ...prev };
        delete newState[assignmentId];
        return newState;
      });
    }
  };

  const handleConfirmInvoicedTechEdit = async () => {
    if (!pendingInvoicedTechEdit || invoicedTechEditConfirmingRef.current) return;
    invoicedTechEditConfirmingRef.current = true;
    setConfirmingInvoicedTechEdit(true);
    const pending = pendingInvoicedTechEdit;
    try {
      await handleUpdateTechnician(pending.assignmentId, pending.field, pending.value, {
        skipInvoicedWarn: true,
      });
    } finally {
      setPendingInvoicedTechEdit(null);
      setConfirmingInvoicedTechEdit(false);
      invoicedTechEditConfirmingRef.current = false;
    }
  };

  const handleDiscardInvoicedTechEdit = () => {
    if (invoicedTechEditConfirmingRef.current) return;
    const pending = pendingInvoicedTechEdit;
    setPendingInvoicedTechEdit(null);
    if (pending?.field === 'tech_cost') {
      const serverValue = serverValues[pending.assignmentId]?.tech_cost ?? '';
      setAssignments(prev => prev.map(a =>
        a.id === pending.assignmentId ? { ...a, tech_cost: serverValue } : a
      ));
    }
  };

  const handleStatusChange = (assignmentId, newStatus) => {
    const assignment = assignments.find((a) => a.id === assignmentId);
    if (!assignment) return;

    if (needsFreeOfChargeConfirm(newStatus)) {
      setFreeOfChargeConfirm({ assignmentId, previousStatus: assignment.status });
      return;
    }

    handleUpdateTechnician(assignmentId, 'status', newStatus);
  };

  const handleConfirmFreeOfCharge = async () => {
    if (!freeOfChargeConfirm || freeOfChargeConfirmingRef.current) return;
    freeOfChargeConfirmingRef.current = true;
    const { assignmentId } = freeOfChargeConfirm;
    setFreeOfChargeConfirm(null);
    try {
      await handleUpdateTechnician(assignmentId, 'status', FREE_OF_CHARGE_STATUS);
    } finally {
      freeOfChargeConfirmingRef.current = false;
    }
  };

  const handleDiscardFreeOfCharge = () => {
    setFreeOfChargeConfirm(null);
  };

  // Lock technician assignment
  const handleLock = async (assignmentId) => {
    if (!jobId) return;
    if (isTabLocked) return;

    setSavingStates(prev => ({ ...prev, [assignmentId]: 'locking' }));
    setError('');

    try {
      const { data, status, message } = await POST(
        `${api.lockJobTechnician}/${jobId}/technicians/${assignmentId}/lock`,
        {},
        {}
      );

      if (status === 200) {
        dispatch(updateJobTechnician({ jobId, technicianId: assignmentId, updatedData: data }));
        setAssignments(prev => prev.map(a => a.id === assignmentId ? data : a));
      } else {
        setError(message || 'Failed to lock technician assignment');
        if (onRefreshJob) onRefreshJob();
      }
    } catch (err) {
      setError('Failed to lock technician assignment');
      console.error('Error locking technician:', err);
      if (onRefreshJob) onRefreshJob();
    } finally {
      setSavingStates(prev => {
        const newState = { ...prev };
        delete newState[assignmentId];
        return newState;
      });
    }
  };

  // Unlock technician assignment
  const handleUnlock = async (assignmentId) => {
    if (!jobId) return;
    if (isTabLocked) return;

    setSavingStates(prev => ({ ...prev, [assignmentId]: 'unlocking' }));
    setError('');

    try {
      const { data, status, message } = await POST(
        `${api.unlockJobTechnician}/${jobId}/technicians/${assignmentId}/unlock`,
        {},
        {}
      );

      if (status === 200) {
        dispatch(updateJobTechnician({ jobId, technicianId: assignmentId, updatedData: data }));
        setAssignments(prev => prev.map(a => a.id === assignmentId ? data : a));
      } else {
        setError(message || 'Failed to unlock technician assignment');
        if (onRefreshJob) onRefreshJob();
      }
    } catch (err) {
      setError('Failed to unlock technician assignment');
      console.error('Error unlocking technician:', err);
      if (onRefreshJob) onRefreshJob();
    } finally {
      setSavingStates(prev => {
        const newState = { ...prev };
        delete newState[assignmentId];
        return newState;
      });
    }
  };

  // Request unlock for technician assignment
  const handleRequestUnlock = async (assignmentId) => {
    if (!jobId) return;
    if (isTabLocked) return;

    setSavingStates(prev => ({ ...prev, [assignmentId]: 'requesting' }));
    setError('');

    try {
      const { data, status, message } = await POST(
        `${api.requestUnlockJobTechnician}/${jobId}/technicians/${assignmentId}/request-unlock`,
        {},
        {}
      );

      if (status === 200) {
        dispatch(updateJobTechnician({ jobId, technicianId: assignmentId, updatedData: data }));
        setAssignments(prev => prev.map(a => a.id === assignmentId ? data : a));
      } else {
        setError(message || 'Failed to request unlock');
        if (onRefreshJob) onRefreshJob();
      }
    } catch (err) {
      setError('Failed to request unlock');
      console.error('Error requesting unlock:', err);
      if (onRefreshJob) onRefreshJob();
    } finally {
      setSavingStates(prev => {
        const newState = { ...prev };
        delete newState[assignmentId];
        return newState;
      });
    }
  };

  // Handle file upload
  const handleFileUpload = async (assignmentId, file) => {
    if (!jobId || !file) return;

    setUploadingAttachment(prev => ({ ...prev, [assignmentId]: true }));
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'Other');

      const { status, message } = await UPLOAD(
        `${api.uploadJobTechnicianAttachment}/${jobId}/technicians/${assignmentId}/attachments`,
        formData
      );

      if (status === 200 || status === 201) {
        // Refresh attachments
        await fetchAttachments(assignmentId);
      } else {
        setError(message || 'Failed to upload attachment');
        if (onRefreshJob) onRefreshJob();
      }
    } catch (err) {
      setError('Failed to upload attachment');
      console.error('Error uploading attachment:', err);
      if (onRefreshJob) onRefreshJob();
    } finally {
      setUploadingAttachment(prev => ({ ...prev, [assignmentId]: false }));
      // Reset file input
      if (fileInputRefs.current[assignmentId]) {
        fileInputRefs.current[assignmentId].value = '';
      }
    }
  };

  // Handle attachment delete
  const handleDeleteAttachment = async (assignmentId, attachmentId) => {
    if (!jobId) return;
    if (isTabLocked) return;

    try {
      const { status, message } = await DELETE(
        `${api.deleteJobTechnicianAttachment}/${jobId}/technicians/${assignmentId}/attachments/${attachmentId}`
      );

      if (status === 200) {
        // Remove from local state
        setAttachments(prev => ({
          ...prev,
          [assignmentId]: prev[assignmentId]?.filter(a => a.id !== attachmentId) || []
        }));
      } else {
        setError(message || 'Failed to delete attachment');
      }
    } catch (err) {
      setError('Failed to delete attachment');
      console.error('Error deleting attachment:', err);
    }
  };

  // Get presigned URL for attachment
  const getPresignedUrl = async (attachmentId) => {
    try {
      const { data, status } = await GET(`${api.getAttachmentDownloadUrl}/${attachmentId}`);
      if (status === 200) {
        return data.data.downloadUrl;
      }
      return null;
    } catch (err) {
      console.error('Error getting presigned URL:', err);
      return null;
    }
  };

  // Handle attachment preview
  const handleAttachmentPreview = async (attachment, assignmentId) => {
    setLoadingPreview(true);
    setPreviewAttachment(attachment);
    setPreviewAssignmentId(assignmentId);
    setPreviewOpen(true);
    
    const presignedUrl = await getPresignedUrl(attachment.id);
    if (presignedUrl) {
      setPreviewUrl(presignedUrl);
    } else {
      setError('Failed to load preview');
      setPreviewOpen(false);
    }
    setLoadingPreview(false);
  };

  // Close preview
  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewAttachment(null);
    setPreviewAssignmentId(null);
    setPreviewUrl(null);
  };

  // Handle attachment download
  const handleAttachmentDownload = async (attachment) => {
    const presignedUrl = await getPresignedUrl(attachment.id);
    if (presignedUrl) {
      try {
        const response = await fetch(presignedUrl);
        const blob = await response.blob();
        
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = attachment.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (err) {
        console.error('Download error:', err);
        setError('Failed to download attachment');
      }
    } else {
      setError('Failed to download attachment');
    }
  };

  // Get file icon based on file type (for list view - smaller size)
  const getFileIcon = (fileType, size = 28) => {
    if (fileType?.startsWith('image/')) {
      return <ImageIcon sx={{ fontSize: size, color: '#10b981' }} />;
    } else if (fileType === 'application/pdf') {
      return <PictureAsPdfIcon sx={{ fontSize: size, color: '#ef4444' }} />;
    } else if (fileType?.includes('word') || fileType?.includes('document')) {
      return <DescriptionIcon sx={{ fontSize: size, color: '#3b82f6' }} />;
    } else if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) {
      return <TableChartIcon sx={{ fontSize: size, color: '#10b981' }} />;
    } else if (fileType?.startsWith('text/')) {
      return <TextSnippetIcon sx={{ fontSize: size, color: '#6b7280' }} />;
    } else {
      return <InsertDriveFileIcon sx={{ fontSize: size, color: isDarkMode ? '#60a5fa' : '#3b82f6' }} />;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Assigned Technicians
        </Typography>
      </Box>
      
      {error && (
        <Box sx={{ 
          p: 2, 
          bgcolor: isDarkMode ? 'rgba(211, 47, 47, 0.1)' : '#ffebee', 
          borderRadius: 1,
          border: isDarkMode ? '1px solid rgba(211, 47, 47, 0.3)' : '1px solid #ef5350'
        }}>
          <Typography variant="body2" sx={{ color: isDarkMode ? '#ef5350' : '#c62828' }}>
            {error}
          </Typography>
        </Box>
      )}

      {/* Loading state for technicians tab */}
      {loadingAdditionalData && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Array.from({ length: 2 }).map((_, index) => (
            <Box key={index} sx={{ p: 3, border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0', borderRadius: 2, backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : '#fafafa' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <Box>
                  <Skeleton variant="text" width={60} height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width={120} height={24} />
                </Box>
                <Box>
                  <Skeleton variant="text" width={60} height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width={100} height={24} />
                </Box>
                <Box>
                  <Skeleton variant="text" width={60} height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width={80} height={24} />
                </Box>
                <Box>
                  <Skeleton variant="text" width={60} height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width={100} height={24} />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}
      
      {/* Add Technician - prominent card with styled autocomplete */}
      {!loadingAdditionalData && jobId && (
        <Box
          onClick={() => !isTabLocked && autocompleteInputRef.current?.focus()}
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
            bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
            transition: 'border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease',
            cursor: isTabLocked ? 'not-allowed' : 'text',
            '&:hover': {
              borderColor: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)',
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
              boxShadow: isDarkMode
                ? '0 2px 12px rgba(0,0,0,0.25)'
                : '0 2px 12px rgba(0,0,0,0.06)',
            },
            '&:focus-within': {
              borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
              boxShadow: isDarkMode
                ? '0 0 0 1px rgba(255,255,255,0.12), 0 4px 16px rgba(0,0,0,0.3)'
                : '0 0 0 1px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.08)',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                borderRadius: 2.5,
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                color: isDarkMode ? '#f1f5f9' : '#0f172a',
              }}
            >
              <PersonAddIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: isDarkMode ? '#f1f5f9' : '#0f172a', letterSpacing: '-0.01em' }}>
                Add technician
              </Typography>
              <Typography variant="body2" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', mt: 0.25, fontSize: '0.8125rem' }}>
                Search to select existing or type a name to create new
              </Typography>
            </Box>
          </Box>
          <Autocomplete
            value={null}
            inputValue={technicianInputValue}
            onInputChange={(event, newInputValue) => {
              setTechnicianInputValue(newInputValue);
            }}
            onChange={(event, value) => {
              if (value) {
                if (value && typeof value === 'object' && value.inputValue) {
                  setDialogOpen(true);
                  setDialogValue({ name: value.inputValue });
                } else {
                  handleAddTechnician(value);
                }
              }
            }}
            filterOptions={(options, params) => {
              const filtered = createFilterOptions()(options, params);
              const { inputValue } = params;
              const isExisting = options.some((option) => {
                if (option && typeof option === 'object') {
                  return inputValue === getTechnicianDisplayName(option);
                }
                return false;
              });
              if (inputValue !== '' && !isExisting) {
                filtered.push({
                  inputValue,
                  label: `Add "${inputValue}"`,
                });
              }
              return filtered;
            }}
            options={(technicians || []).filter(isTechnicianAssignable)}
            getOptionLabel={(option) => {
              if (option.inputValue) {
                return option.inputValue;
              }
              return getTechnicianDisplayName(option);
            }}
            isOptionEqualToValue={(option, value) => {
              if (option && typeof option === 'object' && value && typeof value === 'object') {
                return option.id === value.id;
              }
              return false;
            }}
            disabled={savingStates.adding || isTabLocked}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            autoHighlight
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  <Typography variant="body1">
                    {option.label || getTechnicianDisplayName(option)}
                  </Typography>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={(el) => {
                  autocompleteInputRef.current = el;
                  if (params.inputRef) {
                    if (typeof params.inputRef === 'function') params.inputRef(el);
                    else params.inputRef.current = el;
                  }
                }}
                placeholder="Search or add a technician..."
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#ffffff',
                    fontSize: '0.9375rem',
                    '& fieldset': {
                      borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)',
                    },
                    '&:hover fieldset': {
                      borderColor: isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.22)',
                    },
                    '&.Mui-focused fieldset': {
                      borderWidth: 1,
                      borderColor: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)',
                    },
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mr: 1,
                          color: isDarkMode ? '#94a3b8' : '#64748b',
                        }}
                      >
                        <PersonAddIcon sx={{ fontSize: 20, opacity: 0.7 }} />
                      </Box>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {savingStates.adding && <CircularProgress size={20} sx={{ mr: 0.5 }} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      )}
      
      {/* Display Technicians */}
      {!loadingAdditionalData && assignments.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {assignments.map((assignment, index) => {
            const technicianName = getTechnicianDisplayName(assignment);
            const needsAttention = technicianNeedsAttention(assignment);
            const attentionReasons = getAttentionReasons(assignment);
            const isSaving = savingStates[assignment.id] === 'saving';
            const isDeleting = savingStates[assignment.id] === 'deleting';
            const isLocking = savingStates[assignment.id] === 'locking';
            const isUnlocking = savingStates[assignment.id] === 'unlocking';
            const isRequesting = savingStates[assignment.id] === 'requesting';
            const isLocked = assignment.is_locked;
            const hasUnlockRequest = assignment.requested_unlock;
            const jobBlocksEdits = isTabLocked && !isAccounting(user);
            const isFieldDisabled =
              jobBlocksEdits ||
              isSaving ||
              isDeleting ||
              isLocking ||
              isUnlocking ||
              isRequesting ||
              (isLocked && !canEditLocked(user));
            const deleteDisabled =
              isTabLocked ||
              isDeleting ||
              (isLocked && !canDeleteLocked(user));
            const lockReady = canBeLocked(assignment);
            const visitSummary = formatAssignmentDateSummary(assignment.scheduled_date);
            const paidSummary = formatAssignmentDateSummary(assignment.paid_date);
            const techCostSummary = formatTechCostSummary(assignment.tech_cost);
            
            return (
              <Accordion 
                key={assignment.id}
                defaultExpanded={index === 0}
                disabled={isDeleting}
                onChange={(e, expanded) => {
                  if (expanded && !attachments[assignment.id]) {
                    fetchAttachments(assignment.id);
                  }
                }}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  opacity: isDeleting ? 0.5 : 1,
                  boxShadow: isLocked
                    ? isDarkMode 
                      ? '0 4px 12px rgba(33, 150, 243, 0.3)' 
                      : '0 4px 12px rgba(33, 150, 243, 0.2)'
                    : needsAttention 
                      ? isDarkMode 
                        ? '0 4px 12px rgba(255, 152, 0, 0.3)' 
                        : '0 4px 12px rgba(255, 152, 0, 0.2)'
                      : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  border: isLocked
                    ? isDarkMode 
                      ? '2px solid rgba(33, 150, 243, 0.5)' 
                      : '2px solid rgba(33, 150, 243, 0.4)'
                    : needsAttention 
                      ? isDarkMode 
                        ? '2px solid rgba(255, 152, 0, 0.5)' 
                        : '2px solid rgba(255, 152, 0, 0.4)'
                      : 'none',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: '0 0 16px 0',
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    background: isLocked
                      ? isDarkMode 
                        ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(33, 150, 243, 0.05) 100%)'
                        : 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(227, 242, 253, 0.5) 100%)'
                      : needsAttention
                        ? isDarkMode 
                          ? 'linear-gradient(135deg, rgba(255, 152, 0, 0.15) 0%, rgba(255, 152, 0, 0.05) 100%)'
                          : 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 243, 224, 0.5) 100%)'
                        : isDarkMode 
                          ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
                          : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    borderRadius: '8px 8px 0 0',
                    '&.Mui-expanded': {
                      borderRadius: '8px 8px 0 0',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2, gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                      {isLocked && (
                        <LockIcon 
                          sx={{ 
                            color: isDarkMode ? '#64b5f6' : '#1976d2',
                            fontSize: 24,
                          }} 
                        />
                      )}
                      {!isLocked && needsAttention && (
                        <PriorityHighIcon 
                          sx={{ 
                            color: isDarkMode ? '#ff9800' : '#f57c00',
                            fontSize: 28,
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                              '0%, 100%': { opacity: 1 },
                              '50%': { opacity: 0.6 }
                            }
                          }} 
                        />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: isDarkMode ? '#f1f5f9' : '#333' }}>
                          {technicianName} {assignments.filter(a => a.technician_id === assignment.technician_id).length > 1 && 
                            `(${assignments.filter(a => a.technician_id === assignment.technician_id).indexOf(assignment) + 1})`}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                          {isLocked && (
                            <Chip
                              icon={<LockIcon sx={{ fontSize: 14 }} />}
                              label={`Locked by ${assignment.locked_by || 'Unknown'}`}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                bgcolor: isDarkMode ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.15)',
                                color: isDarkMode ? '#64b5f6' : '#1565c0',
                                fontWeight: 600,
                                border: isDarkMode ? '1px solid rgba(33, 150, 243, 0.3)' : '1px solid rgba(33, 150, 243, 0.4)'
                              }}
                            />
                          )}
                          {hasUnlockRequest && (
                            <Chip
                              label={`Unlock requested by ${assignment.requested_unlock_by || 'Unknown'}`}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                bgcolor: isDarkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.15)',
                                color: isDarkMode ? '#ce93d8' : '#7b1fa2',
                                fontWeight: 600,
                                border: isDarkMode ? '1px solid rgba(156, 39, 176, 0.3)' : '1px solid rgba(156, 39, 176, 0.4)'
                              }}
                            />
                          )}
                          {!isLocked && needsAttention && (
                            <Chip
                              icon={<WarningIcon sx={{ fontSize: 16 }} />}
                              label={attentionReasons.join(', ')}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.15)',
                                color: isDarkMode ? '#ffb74d' : '#e65100',
                                fontWeight: 600,
                                border: isDarkMode ? '1px solid rgba(255, 152, 0, 0.3)' : '1px solid rgba(255, 152, 0, 0.4)'
                              }}
                            />
                          )}
                        </Box>
                        <Box
                          component="div"
                          sx={{
                            mt: 0.75,
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 0.75,
                            rowGap: 0.25,
                            maxWidth: '100%',
                            '.MuiAccordionSummary-root.Mui-expanded &': {
                              display: 'none',
                            },
                          }}
                        >
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: isDarkMode ? '#cbd5e1' : '#475569',
                              fontSize: '0.8125rem',
                            }}
                          >
                            {getJobTechnicianStatusLabel(assignment.status)}
                          </Typography>
                          {visitSummary && (
                            <>
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ color: isDarkMode ? '#64748b' : '#94a3b8', fontSize: '0.75rem' }}
                              >
                                ·
                              </Typography>
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.8125rem' }}
                              >
                                Visit {visitSummary}
                              </Typography>
                            </>
                          )}
                          {paidSummary && (
                            <>
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ color: isDarkMode ? '#64748b' : '#94a3b8', fontSize: '0.75rem' }}
                              >
                                ·
                              </Typography>
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.8125rem' }}
                              >
                                Paid {paidSummary}
                              </Typography>
                            </>
                          )}
                          {techCostSummary && (
                            <>
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ color: isDarkMode ? '#64748b' : '#94a3b8', fontSize: '0.75rem' }}
                              >
                                ·
                              </Typography>
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.8125rem' }}
                              >
                                {techCostSummary}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                      {(isSaving || isLocking || isUnlocking || isRequesting) && <CircularProgress size={20} sx={{ mr: 1 }} />}
                    </Box>
                    <IconButton
                      component="div"
                      role="button"
                      tabIndex={deleteDisabled ? -1 : 0}
                      aria-disabled={deleteDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (deleteDisabled) return;
                        handleRemoveTechnician(assignment.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        e.stopPropagation();
                        if (deleteDisabled) return;
                        handleRemoveTechnician(assignment.id);
                      }}
                      size="small"
                      sx={{
                        color: '#dc3545',
                        ...(deleteDisabled
                          ? { opacity: 0.38, pointerEvents: 'none' }
                          : {}),
                      }}
                    >
                      {isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                    </IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ 
                  p: 3, 
                  background: isDarkMode 
                    ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
                    : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Lock/Unlock Actions Row */}
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: 1 }}>
                      {!isLocked && canLock(user) && (
                        <Tooltip title={!lockReady ? "Set paid date and tech cost > 0 to enable locking" : "Lock this assignment"}>
                          <span>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={isLocking ? <CircularProgress size={16} /> : <LockIcon />}
                              onClick={() => handleLock(assignment.id)}
                              disabled={isTabLocked || !lockReady || isLocking || isSaving}
                              sx={{
                                borderColor: isDarkMode ? '#64b5f6' : '#1976d2',
                                color: isDarkMode ? '#64b5f6' : '#1976d2',
                                '&:hover': {
                                  borderColor: isDarkMode ? '#90caf9' : '#1565c0',
                                  bgcolor: isDarkMode ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.08)',
                                },
                                '&.Mui-disabled': {
                                  borderColor: isDarkMode ? '#455a64' : '#bdbdbd',
                                  color: isDarkMode ? '#455a64' : '#bdbdbd',
                                }
                              }}
                            >
                              Lock
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {isLocked && canUnlock(user) && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={isUnlocking ? <CircularProgress size={16} /> : <LockOpenIcon />}
                          onClick={() => handleUnlock(assignment.id)}
                          disabled={isTabLocked || isUnlocking || isSaving}
                          sx={{
                            borderColor: isDarkMode ? '#81c784' : '#4caf50',
                            color: isDarkMode ? '#81c784' : '#4caf50',
                            '&:hover': {
                              borderColor: isDarkMode ? '#a5d6a7' : '#388e3c',
                              bgcolor: isDarkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.08)',
                            }
                          }}
                        >
                          Unlock
                        </Button>
                      )}
                      {isLocked && !canUnlock(user) && !hasUnlockRequest && !canEditLocked(user) && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={isRequesting ? <CircularProgress size={16} /> : <LockOpenIcon />}
                          onClick={() => handleRequestUnlock(assignment.id)}
                          disabled={isTabLocked || isRequesting || isSaving}
                          sx={{
                            borderColor: isDarkMode ? '#ce93d8' : '#9c27b0',
                            color: isDarkMode ? '#ce93d8' : '#9c27b0',
                            '&:hover': {
                              borderColor: isDarkMode ? '#e1bee7' : '#7b1fa2',
                              bgcolor: isDarkMode ? 'rgba(156, 39, 176, 0.1)' : 'rgba(156, 39, 176, 0.08)',
                            }
                          }}
                        >
                          Request Unlock
                        </Button>
                      )}
                    </Box>

                    {/* Status Row */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Autocomplete 
                        options={TECHNICIAN_STATUS_OPTIONS}
                        value={{ value: assignment.status, label: getJobTechnicianStatusLabel(assignment.status) }}
                        onChange={(event, value) => handleStatusChange(assignment.id, value?.value || 1)}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        renderOption={(props, option) => {
                          const { key, ...optionProps } = props;
                          const isReselectingFreeOfCharge = option.value === FREE_OF_CHARGE_STATUS
                            && Number(assignment.status) === FREE_OF_CHARGE_STATUS;
                          return (
                            <li
                              key={key}
                              {...optionProps}
                              onClick={(event) => {
                                if (isReselectingFreeOfCharge) {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setFreeOfChargeConfirm({
                                    assignmentId: assignment.id,
                                    previousStatus: assignment.status,
                                  });
                                  return;
                                }
                                optionProps.onClick?.(event);
                              }}
                            >
                              {option.label}
                            </li>
                          );
                        }}
                        renderInput={(params) => <TextField {...params} label="Status" />}
                        sx={{ flex: 1 }}
                        disabled={isFieldDisabled}
                      />
                    </Box>
                    
                    {/* Visit Date and Paid Date — stacked on mobile/tablet, side-by-side on desktop */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2 }}>
                      <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
                        <DateTimePicker
                          label="Visit Date"
                          value={assignment.scheduled_date ? dayjs(assignment.scheduled_date) : null}
                          onChange={(newValue) => {
                            const dateString = newValue ? newValue.toISOString() : null;
                            handleUpdateTechnician(assignment.id, 'scheduled_date', dateString);
                          }}
                          disabled={isFieldDisabled}
                          slotProps={{
                            textField: {
                              error: !!dateErrors[`${assignment.id}-scheduled_date`],
                              helperText: dateErrors[`${assignment.id}-scheduled_date`],
                              fullWidth: true,
                            }
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
                        <DateTimePicker
                          label="Paid Date"
                          value={assignment.paid_date ? dayjs(assignment.paid_date) : null}
                          onChange={(newValue) => {
                            const dateString = newValue ? newValue.toISOString() : null;
                            handleUpdateTechnician(assignment.id, 'paid_date', dateString);
                          }}
                          disabled={isFieldDisabled}
                          slotProps={{
                            textField: {
                              error: !!dateErrors[`${assignment.id}-paid_date`],
                              helperText: dateErrors[`${assignment.id}-paid_date`],
                              fullWidth: true,
                            }
                          }}
                        />
                      </Box>
                    </Box>
                    
                    {/* Tech Cost Row */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField 
                        label="Tech Cost" 
                        type="number" 
                        value={assignment.tech_cost || ''} 
                        onChange={(e) => {
                          // Update local state only on change
                          setAssignments(prev => prev.map(a => 
                            a.id === assignment.id ? { ...a, tech_cost: e.target.value } : a
                          ));
                        }}
                        onBlur={(e) => {
                          // Save to server on blur if changed from server value
                          const serverValue = serverValues[assignment.id]?.tech_cost || '';
                          // eslint-disable-next-line eqeqeq
                          if (e.target.value != serverValue) { // Use != to handle string/number comparison
                            handleUpdateTechnician(assignment.id, 'tech_cost', e.target.value);
                          }
                        }}
                        onFocus={(e) => {e.target.select()}}
                        onWheel={(e) => e.target.blur()}
                        disabled={isFieldDisabled}
                        sx={{ flex: 1 }}
                        slotProps={{
                          input: {
                            startAdornment: <Box sx={{ color: '#90caf9', fontWeight: 700, mr: 0.5 }}>$</Box>,
                          },
                          htmlInput: {
                            min: 0,
                            step: '0.01',
                          },
                        }}
                      />
                    </Box>
                    
                    {/* Notes */}
                    <TextField 
                      label="Notes" 
                      multiline 
                      rows={3} 
                      value={assignment.notes || ''} 
                      onChange={(e) => {
                        // Update local state only on change
                        setAssignments(prev => prev.map(a => 
                          a.id === assignment.id ? { ...a, notes: e.target.value } : a
                        ));
                      }}
                      onBlur={(e) => {
                        // Save to server on blur if changed from server value
                        const serverValue = serverValues[assignment.id]?.notes || '';
                        if (e.target.value !== serverValue) {
                          handleUpdateTechnician(assignment.id, 'notes', e.target.value);
                        }
                      }}
                      placeholder="Add notes for this technician assignment..."
                      disabled={isFieldDisabled}
                    />

                    {/* Attachments Section */}
                    <Divider sx={{ my: 2 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachFileIcon fontSize="small" />
                        Attachments {attachments[assignment.id]?.length ? `(${attachments[assignment.id].length})` : ''}
                      </Typography>
                      {canUploadAttachment(user) && (
                        <Box sx={{ mb: 2 }}>
                          <input
                            type="file"
                            ref={el => fileInputRefs.current[assignment.id] = el}
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                handleFileUpload(assignment.id, e.target.files[0]);
                              }
                            }}
                            style={{ display: 'none' }}
                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
                          />
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={uploadingAttachment[assignment.id] ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                            onClick={() => fileInputRefs.current[assignment.id]?.click()}
                            disabled={uploadingAttachment[assignment.id]}
                            sx={{ textTransform: 'none' }}
                          >
                            Upload Files
                          </Button>
                        </Box>
                      )}

                      {loadingAttachments[assignment.id] ? (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Skeleton variant="rectangular" width={140} height={160} sx={{ borderRadius: 2 }} />
                          <Skeleton variant="rectangular" width={140} height={160} sx={{ borderRadius: 2 }} />
                        </Box>
                      ) : attachments[assignment.id]?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap' }}>
                          {attachments[assignment.id].map(attachment => {
                            const fileExtension = attachment.file_name?.split('.').pop()?.toUpperCase() || 'FILE';
                            
                            return (
                              <Box
                                key={attachment.id}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: 140,
                                  minHeight: 160,
                                  p: 2,
                                  border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                                  borderRadius: 2,
                                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : '#ffffff',
                                  transition: 'all 0.2s ease',
                                  position: 'relative',
                                  '&:hover': {
                                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8f9fa',
                                    borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                                    transform: 'translateY(-2px)',
                                    boxShadow: isDarkMode 
                                      ? '0 4px 12px rgba(0,0,0,0.3)' 
                                      : '0 4px 12px rgba(0,0,0,0.15)',
                                  }
                                }}
                              >
                                {/* File Icon */}
                                <Box
                                  onClick={() => handleAttachmentPreview(attachment, assignment.id)}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    mb: 1,
                                    cursor: 'pointer',
                                    flex: 1,
                                  }}
                                >
                                  {getFileIcon(attachment.file_type, 40)}
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      fontSize: '0.65rem',
                                      fontWeight: 700,
                                      color: isDarkMode ? '#64b5f6' : '#1976d2',
                                      letterSpacing: '0.5px',
                                    }}
                                  >
                                    {fileExtension}
                                  </Typography>
                                </Box>

                                {/* File Name */}
                                <Typography
                                  variant="body2"
                                  onClick={() => handleAttachmentPreview(attachment, assignment.id)}
                                  sx={{
                                    fontWeight: 600,
                                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    width: '100%',
                                    textAlign: 'center',
                                    fontSize: '0.85rem',
                                    mb: 1,
                                    cursor: 'pointer',
                                  }}
                                >
                                  {attachment.file_name}
                                </Typography>

                                {/* Action Buttons */}
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    gap: 0.5, 
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Tooltip title="Preview">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAttachmentPreview(attachment, assignment.id)}
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.08)',
                                        '&:hover': {
                                          bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)',
                                        }
                                      }}
                                    >
                                      <VisibilityIcon sx={{ fontSize: 16, color: isDarkMode ? '#60a5fa' : '#2563eb' }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Download">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAttachmentDownload(attachment)}
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        bgcolor: isDarkMode ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.08)',
                                        '&:hover': {
                                          bgcolor: isDarkMode ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.15)',
                                        }
                                      }}
                                    >
                                      <DownloadIcon sx={{ fontSize: 16, color: isDarkMode ? '#4ade80' : '#16a34a' }} />
                                    </IconButton>
                                  </Tooltip>
                                  {canDeleteAttachment(user) && (
                                    <Tooltip title="Delete">
                                      <IconButton
                                        size="small"
                                        onClick={() => !isTabLocked && handleDeleteAttachment(assignment.id, attachment.id)}
                                        disabled={isTabLocked}
                                        sx={{
                                          width: 28,
                                          height: 28,
                                          bgcolor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.08)',
                                          '&:hover': {
                                            bgcolor: isDarkMode ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.15)',
                                          }
                                        }}
                                      >
                                        <DeleteIcon sx={{ fontSize: 16, color: isDarkMode ? '#f87171' : '#dc2626' }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: isDarkMode ? '#64748b' : '#9e9e9e', fontStyle: 'italic' }}>
                          No attachments yet
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      ) : !loadingAdditionalData ? (
        <Typography variant="body1" sx={{ color: '#666', fontStyle: 'italic' }}>
          No technicians assigned to this job. Use the field above to add technicians.
        </Typography>
      ) : null}

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
            backgroundImage: 'none',
            minHeight: '80vh',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
          pb: 2,
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {previewAttachment?.file_name}
            </Typography>
            <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#666' }}>
              {previewAttachment?.file_type}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Download">
              <IconButton
                onClick={() => handleAttachmentDownload(previewAttachment)}
                sx={{ color: isDarkMode ? '#81c784' : '#4caf50' }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            {canDeleteAttachment(user) && previewAssignmentId && (
              <Tooltip title="Delete">
                <IconButton
                  onClick={() => {
                    if (isTabLocked) return;
                    handleDeleteAttachment(previewAssignmentId, previewAttachment.id);
                    handleClosePreview();
                  }}
                  disabled={isTabLocked}
                  sx={{ color: isDarkMode ? '#f87171' : '#dc2626' }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={handleClosePreview} sx={{ color: isDarkMode ? '#94a3b8' : '#666' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: isDarkMode ? '#0f172a' : '#f5f5f5',
          position: 'relative',
        }}>
          {loadingPreview ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="body2" sx={{ color: isDarkMode ? '#94a3b8' : '#666' }}>
                Loading preview...
              </Typography>
            </Box>
          ) : previewUrl ? (
            <>
              {previewAttachment?.file_type?.startsWith('image/') ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt={previewAttachment?.file_name}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    p: 2,
                  }}
                />
              ) : previewAttachment?.file_type === 'application/pdf' ? (
                <iframe
                  src={previewUrl}
                  title={previewAttachment?.file_name}
                  style={{
                    width: '100%',
                    height: '70vh',
                    border: 'none',
                  }}
                />
              ) : previewAttachment?.file_type?.startsWith('text/') ? (
                <Box sx={{ 
                  width: '100%', 
                  height: '70vh', 
                  overflow: 'auto',
                  p: 3,
                  bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
                }}>
                  <iframe
                    src={previewUrl}
                    title={previewAttachment?.file_name}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 3,
                  p: 4,
                }}>
                  {getFileIcon(previewAttachment?.file_type, 80)}
                  <Typography variant="h6" sx={{ color: isDarkMode ? '#f1f5f9' : '#333' }}>
                    {previewAttachment?.file_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDarkMode ? '#94a3b8' : '#666' }}>
                    Preview not available for this file type
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleAttachmentDownload(previewAttachment)}
                    sx={{
                      bgcolor: isDarkMode ? '#2563eb' : '#1976d2',
                      '&:hover': {
                        bgcolor: isDarkMode ? '#1d4ed8' : '#1565c0',
                      }
                    }}
                  >
                    Download File
                  </Button>
                </Box>
              )}
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(freeOfChargeConfirm)}
        onClose={handleDiscardFreeOfCharge}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark as Free of charge?</DialogTitle>
        <DialogContent>
          <Typography>
            Selecting Free of charge will lock this technician assignment and set the cost to $0.
            The paid date will be set to today.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDiscardFreeOfCharge}>Discard</Button>
          <Button variant="contained" onClick={handleConfirmFreeOfCharge} color="primary">
            Accept
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingInvoicedTechEdit)}
        onClose={confirmingInvoicedTechEdit ? undefined : handleDiscardInvoicedTechEdit}
        aria-labelledby="invoiced-tech-edit-warn-title"
      >
        <DialogTitle id="invoiced-tech-edit-warn-title">
          Edit technician on invoiced job
        </DialogTitle>
        <DialogContent>
          <Typography>
            {getInvoicedTechEditWarningText()}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDiscardInvoicedTechEdit}
            disabled={confirmingInvoicedTechEdit}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmInvoicedTechEdit}
            variant="contained"
            color="warning"
            disabled={confirmingInvoicedTechEdit}
          >
            {confirmingInvoicedTechEdit ? 'Saving…' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Technician Dialog */}
      <AddTechnicianDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onTechnicianAdded={handleTechnicianAdded}
        initialName={dialogValue.name}
      />
    </Box>
  );
};

export default TechniciansTab;
